import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Buildings, FloppyDisk, Upload, Image } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function CompanySection() {
  const [form, setForm] = useState({
    nome: '', indirizzo: '', telefono: '', email: '',
    piva: '', cf: '', pec: '', iban: '', segreteria: '', logo_url: '',
    bookings_enabled: false, instagram_enabled: false, booking_notification_emails: '',
    token_booking_open_at: '', token_booking_close_at: '', public_booking_open_at: '',
    public_booking_anno_accademico: '',
    portineria_enabled: false, portineria_password: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [resCompany, resPortineria] = await Promise.all([
        api.getCompanySettings(),
        api.getPortineriaConfig().catch(() => ({ data: { portineria_enabled: false, portineria_password: '' } })),
      ]);
      setForm({ ...resCompany.data, ...resPortineria.data });
    } catch { toast.error('Errore caricamento dati azienda'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { logo_url, ...data } = form;
      await api.updateCompanySettings(data);
      toast.success('Dati azienda aggiornati');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadCompanyLogo(file);
      setForm(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo aggiornato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload logo'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="text-center py-12 text-[#52525B]">Caricamento...</div>;

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Dati Azienda</h1>
        <p className="text-[#52525B] text-sm">Gestisci le informazioni aziendali visibili nella pagina di login</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in animate-delay-1">
        {/* Logo Card */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#52525B] mb-4">Logo</h2>
          <div className="flex flex-col items-center gap-4">
            {form.logo_url ? (
              <div className="w-full max-w-[200px] aspect-square bg-[#F4F4F5] rounded-sm flex items-center justify-center overflow-hidden">
                <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" />
              </div>
            ) : (
              <div className="w-full max-w-[200px] aspect-square bg-[#F4F4F5] rounded-sm flex items-center justify-center" data-testid="company-logo-placeholder">
                <Image size={48} className="text-[#D4D4D8]" />
              </div>
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="company-logo-upload" />
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-sm hover:bg-[#1a1a1a] transition-colors">
                <Upload size={16} />
                {uploading ? 'Caricamento...' : 'Carica Logo'}
              </span>
            </label>
            <p className="text-xs text-[#A1A1AA] text-center">PNG, JPG, SVG, WebP. Max 5MB.</p>
          </div>
        </div>

        {/* Info Form */}
        <div className="lg:col-span-2 bg-white border border-[#E4E4E7] rounded-sm p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#52525B] mb-4">Informazioni</h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Nome Azienda</Label>
                <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="es. Accademia Fiori Musicali" className="rounded-sm" data-testid="company-nome" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Indirizzo</Label>
                <Input value={form.indirizzo} onChange={e => setForm({ ...form, indirizzo: e.target.value })} placeholder="es. Via Roma, 1 - 20100 Milano" className="rounded-sm" data-testid="company-indirizzo" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Telefono</Label>
                <Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} placeholder="es. 333 1234567" className="rounded-sm" data-testid="company-telefono" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="es. info@azienda.it" className="rounded-sm" data-testid="company-email" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">P.IVA</Label>
                <Input value={form.piva} onChange={e => setForm({ ...form, piva: e.target.value })} placeholder="es. 01234567890" className="rounded-sm font-mono" data-testid="company-piva" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Codice Fiscale</Label>
                <Input value={form.cf} onChange={e => setForm({ ...form, cf: e.target.value })} placeholder="es. 01234567890" className="rounded-sm font-mono" data-testid="company-cf" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">PEC</Label>
                <Input type="email" value={form.pec} onChange={e => setForm({ ...form, pec: e.target.value })} placeholder="es. azienda@pec.it" className="rounded-sm" data-testid="company-pec" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">IBAN</Label>
                <Input value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} placeholder="es. IT..." className="rounded-sm font-mono" data-testid="company-iban" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Orario Segreteria</Label>
                <Input value={form.segreteria} onChange={e => setForm({ ...form, segreteria: e.target.value })} placeholder="es. Lun-Ven 9:00-17:00" className="rounded-sm" data-testid="company-segreteria" />
              </div>
              <div className="sm:col-span-2 border-t border-[#E4E4E7] pt-4 mt-2">
                <h4 className="text-sm font-bold text-[#2D2A4A] uppercase tracking-wider mb-1">Prenotazioni Pubbliche Home Page</h4>
                <p className="text-xs text-[#71717A] mb-3">Lo stato del pulsante "Prenota uno slot lezioni" in home page dipende dalla data di apertura impostata sotto.</p>
              </div>
              <div className="sm:col-span-2">
                {(() => {
                  const raw = form.public_booking_open_at || '';
                  let openAt = null;
                  if (raw) {
                    const t = new Date(raw);
                    if (!isNaN(t.getTime())) openAt = t;
                  }
                  const isFuture = openAt && openAt > new Date();
                  const fmtDate = openAt ? openAt.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
                  const fmtTime = openAt ? openAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
                  const badge = isFuture
                    ? { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', dot: 'bg-orange-500', label: 'CHIUSE', desc: `Apriranno il ${fmtDate} alle ${fmtTime}` }
                    : openAt
                      ? { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400', dot: 'bg-green-500', label: 'APERTE', desc: `Aperte dal ${fmtDate} alle ${fmtTime}` }
                      : { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400', dot: 'bg-green-500', label: 'APERTE', desc: 'Nessuna data impostata — bottone sempre attivo' };
                  return (
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-sm border ${badge.bg} ${badge.border}`} data-testid="company-bookings-badge">
                      <span className={`w-3 h-3 rounded-full ${badge.dot} animate-pulse`} />
                      <span className={`font-extrabold uppercase tracking-widest text-sm ${badge.text}`} data-testid="company-bookings-status">{badge.label}</span>
                      <span className={`text-xs ${badge.text} opacity-80`}>{badge.desc}</span>
                    </div>
                  );
                })()}
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Data e Ora di Apertura Prenotazioni Home Page</Label>
                <Input
                  type="datetime-local"
                  value={form.public_booking_open_at || ''}
                  onChange={e => setForm({ ...form, public_booking_open_at: e.target.value })}
                  className="rounded-sm"
                  data-testid="company-public-open-at"
                />
                <p className="text-xs text-[#A1A1AA]">Prima di questa data il bottone è visibile ma bloccato. Lasciato vuoto = sempre attivo.</p>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Anno Accademico per le Prenotazioni Home Page</Label>
                <select
                  value={form.public_booking_anno_accademico ?? ''}
                  onChange={e => setForm({ ...form, public_booking_anno_accademico: e.target.value })}
                  className="w-full h-10 rounded-sm border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="company-public-booking-anno"
                >
                  <option value="">Automatico (anno corrente)</option>
                  {(() => {
                    const y0 = new Date().getFullYear();
                    return [y0 - 1, y0, y0 + 1, y0 + 2].map(y => (
                      <option key={y} value={y}>{y} / {String(y + 1).slice(-2)}</option>
                    ));
                  })()}
                </select>
                <p className="text-xs text-[#A1A1AA]">L'anno accademico che verrà fissato (e non modificabile) nel form di prenotazione pubblico. Lasciato vuoto = usa l'anno corrente automaticamente.</p>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Email notifiche prenotazioni</Label>
                <Input
                  value={form.booking_notification_emails}
                  onChange={e => setForm({ ...form, booking_notification_emails: e.target.value })}
                  placeholder="es. segreteria@fiorimusicali.it, admin@fiorimusicali.it"
                  className="rounded-sm"
                  data-testid="company-booking-notify"
                />
                <p className="text-xs text-[#A1A1AA]">Uno o più indirizzi (separati da virgola) che riceveranno un'email quando arriva una nuova prenotazione dalla home</p>
              </div>
              <div className="sm:col-span-2 border-t border-[#E4E4E7] pt-4 mt-2">
                <h4 className="text-sm font-bold text-[#2D2A4A] uppercase tracking-wider mb-1">Manutenzione</h4>
                <p className="text-xs text-[#71717A] mb-3">Strumenti una-tantum per allineare i dati storici dopo un aggiornamento software.</p>
                <Button type="button" variant="outline" onClick={async () => {
                  if (!window.confirm('Sincronizzare il flag PROVA sulle lezioni già esistenti generate da prenotazioni PROVA?')) return;
                  try {
                    const res = await api.backfillLessonProva();
                    toast.success(`Sincronizzazione completata: ${res.data?.updated || 0} lezioni allineate (su ${res.data?.scanned || 0} controllate)`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Errore sincronizzazione');
                  }
                }} className="rounded-sm" data-testid="company-backfill-prova-btn">
                  Sincronizza flag PROVA sulle lezioni esistenti
                </Button>
              </div>
              <div className="sm:col-span-2 border-t border-[#E4E4E7] pt-4 mt-2">
                <h4 className="text-sm font-bold text-[#2D2A4A] uppercase tracking-wider mb-1">Finestra Link Personalizzati Alunni</h4>
                <p className="text-xs text-[#71717A] mb-3">Il link `/prenota?token=...` inviato tramite email agli alunni sarà attivo solo tra queste due date. Non influenza la prenotazione pubblica dalla home.</p>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Apertura Prenotazioni con Link</Label>
                <Input
                  type="datetime-local"
                  value={form.token_booking_open_at || ''}
                  onChange={e => setForm({ ...form, token_booking_open_at: e.target.value })}
                  className="rounded-sm"
                  data-testid="company-token-open-at"
                />
                <p className="text-xs text-[#A1A1AA]">Lasciato vuoto = link sempre attivo</p>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Chiusura Prenotazioni con Link</Label>
                <Input
                  type="datetime-local"
                  value={form.token_booking_close_at || ''}
                  onChange={e => setForm({ ...form, token_booking_close_at: e.target.value })}
                  className="rounded-sm"
                  data-testid="company-token-close-at"
                />
                <p className="text-xs text-[#A1A1AA]">Lasciato vuoto = nessuna scadenza</p>
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer py-2">
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.instagram_enabled ? 'bg-green-500' : 'bg-[#D4D4D8]'}`} onClick={() => setForm({ ...form, instagram_enabled: !form.instagram_enabled })} data-testid="company-instagram-toggle">
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.instagram_enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </div>
                  <div>
                    <span className="text-sm font-medium">{form.instagram_enabled ? 'Instagram Attivo' : 'Instagram Disattivato'}</span>
                    <p className="text-xs text-[#A1A1AA]">Mostra la sezione Instagram nella home page</p>
                  </div>
                </label>
              </div>

              {/* Portineria */}
              <div className="sm:col-span-2 mt-3 p-4 border border-[#E4E4E7] rounded-sm bg-[#FAFAFA]">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">Portineria · Pagina Pubblica</div>
                    <p className="text-xs text-[#71717A]">Accesso a <code className="text-[10px] bg-white px-1 py-0.5 rounded">/portineria</code> con password fissa per il personale di segreteria (vista giornaliera orari)</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div className={`relative w-11 h-6 rounded-full transition-colors ${form.portineria_enabled ? 'bg-green-500' : 'bg-[#D4D4D8]'}`} onClick={() => setForm({ ...form, portineria_enabled: !form.portineria_enabled })} data-testid="company-portineria-toggle">
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.portineria_enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      {form.portineria_enabled ? 'Aperta' : 'Chiusa'}
                    </span>
                  </label>
                </div>
                <div className="space-y-1">
                  <Label className="label-overline">Password Portineria</Label>
                  <Input
                    type="text"
                    value={form.portineria_password || ''}
                    onChange={e => setForm({ ...form, portineria_password: e.target.value })}
                    placeholder="Password condivisa"
                    className="rounded-sm bg-white"
                    data-testid="company-portineria-password"
                  />
                  <p className="text-xs text-[#A1A1AA]">Password condivisa mostrata in chiaro — cambiala regolarmente. Se la Portineria è chiusa, l'accesso viene rifiutato anche con password corretta.</p>
                </div>
              </div>
            </div>
            <div className="pt-2">
              <Button type="submit" disabled={saving} className="bg-[#0A0A0A] rounded-sm" data-testid="company-save-btn">
                <FloppyDisk size={18} className="mr-2" />
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
